/**
 * Теры ВЗП: выбор теры у набора и винрейт по терам.
 *
 * Список тер общий для обоих: базовые плюс уже вписанные вручную. Своя тера,
 * один раз вписанная у набора, дальше появляется кнопкой наравне с базовыми.
 */

import { useState, type ReactNode } from 'react';
import { TERRITORY_MAX_LENGTH, territoryWinrate, type TerritoryStats } from '@ak/shared';

import { apiPost } from '../lib/api.ts';
import { useResource } from '../lib/useResource.ts';
import { useToast } from './toast.tsx';
import { Button, Panel, Skeleton, TextInput } from './ui.tsx';

interface TerritoriesResponse {
  territories: TerritoryStats[];
}

function useTerritories() {
  return useResource<TerritoriesResponse>(
    '/api/stats/territories',
    (event) => event.type === 'event.changed' || event.type === 'events.changed',
  );
}

/** Кнопки тер у набора: нажал — отмечено, нажал выбранную ещё раз — снято. */
export function TerritoryPicker({
  eventId,
  territory,
  onChanged,
}: {
  eventId: number;
  territory: string | null;
  onChanged: () => void;
}): ReactNode {
  const toast = useToast();
  const { data } = useTerritories();
  const [busy, setBusy] = useState(false);
  const [custom, setCustom] = useState('');

  const save = (value: string | null): void => {
    setBusy(true);
    apiPost(`/api/events/${String(eventId)}/territory`, { territory: value })
      .then(() => {
        toast.success(value === null ? 'Тера снята' : `Тера: ${value}`);
        setCustom('');
        onChanged();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось отметить теру');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const names = data?.territories.map((entry) => entry.territory) ?? [];
  // Вписанная только что тера может ещё не прийти в общем списке.
  const shown = territory !== null && !names.includes(territory) ? [...names, territory] : names;

  return (
    <Panel className="mt-4 px-4 py-3">
      <div className="mb-2 text-sm font-medium">{territory === null ? 'Какая была тера?' : 'Тера'}</div>

      <div className="flex flex-wrap gap-2">
        {shown.map((name) => {
          const active = name === territory;
          return (
            <Button
              key={name}
              size="sm"
              variant={active ? 'primary' : 'secondary'}
              busy={busy && active}
              disabled={busy}
              aria-pressed={active}
              onClick={() => {
                save(active ? null : name);
              }}
            >
              {active ? '✓ ' : ''}
              {name}
            </Button>
          );
        })}
      </div>

      <form
        className="mt-3 flex flex-wrap items-center gap-2"
        onSubmit={(formEvent) => {
          formEvent.preventDefault();
          const value = custom.replace(/\s+/g, ' ').trim();
          if (value !== '') {
            save(value);
          }
        }}
      >
        <TextInput
          value={custom}
          maxLength={TERRITORY_MAX_LENGTH}
          placeholder="Своя тера, например для 5х5"
          className="max-w-xs"
          onChange={(changeEvent) => {
            setCustom(changeEvent.target.value);
          }}
        />
        <Button size="sm" type="submit" disabled={busy || custom.trim() === ''}>
          Добавить и отметить
        </Button>
      </form>
    </Panel>
  );
}

/** Окно «Винрейт по терам»: полоска побед и поражений, процент, название. */
export function TerritoryWinrate(): ReactNode {
  const { data, loading } = useTerritories();

  const rows = (data?.territories ?? [])
    .filter((entry) => entry.wins + entry.losses + entry.afk > 0)
    .sort((left, right) => right.wins + right.losses - (left.wins + left.losses));

  return (
    <Panel className="p-4">
      <div className="mb-3">
        <div className="text-sm font-semibold">Винрейт по терам</div>
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Победы и поражения ВЗП с отмеченной терой. АФК в процент не идёт.
        </div>
      </div>

      {loading && data === null ? (
        <Skeleton rows={4} />
      ) : rows.length === 0 ? (
        <div className="py-4 text-sm" style={{ color: 'var(--text-muted)' }}>
          Пока пусто: отметь у набора итог и теру — здесь появится статистика.
        </div>
      ) : (
        <ul className="grid gap-3">
          {rows.map((entry) => {
            const winrate = territoryWinrate(entry);
            const decided = entry.wins + entry.losses;
            const winShare = decided === 0 ? 0 : (entry.wins / decided) * 100;

            return (
              <li key={entry.territory}>
                <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate font-medium">{entry.territory}</span>
                  <span className="shrink-0 font-semibold tabular-nums">
                    {winrate === null ? '—' : `${String(winrate)}%`}
                  </span>
                </div>
                <div
                  className="flex h-2 overflow-hidden rounded-full"
                  style={{ background: 'var(--surface-hover)' }}
                  role="img"
                  aria-label={`${entry.territory}: ${String(entry.wins)} побед, ${String(entry.losses)} поражений`}
                >
                  {decided > 0 && (
                    <>
                      <div style={{ width: `${String(winShare)}%`, background: 'var(--positive)' }} />
                      <div style={{ width: `${String(100 - winShare)}%`, background: 'var(--danger)' }} />
                    </>
                  )}
                </div>
                <div className="mt-1 text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
                  {String(entry.wins)} В · {String(entry.losses)} П{entry.afk > 0 ? ` · ${String(entry.afk)} АФК` : ''}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
